import { useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import CallMergeIcon from "@mui/icons-material/CallMerge";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import MilitaryTechOutlinedIcon from "@mui/icons-material/MilitaryTechOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import UndoIcon from "@mui/icons-material/Undo";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useScriptMode } from "../../contexts/ScriptModeContext";
import { useToast } from "../../components/ui/Toast";
import { SoftTag, cls } from "../hatim/soft/SoftKit";
import QuestionRows from "../muzakere/QuestionRows";
import {
  useAccessRequestsQuery,
  useCreateProposalMutation,
  useDeletePackageMutation,
  useForkPackageMutation,
  usePackageDetailQuery,
  usePackageForksQuery,
  usePackageProposalsQuery,
  usePackageStargazersQuery,
  useReportPackageMutation,
  useStarPackageMutation,
  useSubmitPackageMutation,
} from "../../api/libraryApi";
import {
  PROPOSAL_MESSAGE_MAX,
  PROPOSAL_STATUS_KEY,
  forkMeta,
  formatDay,
  ownerReviewAction,
  reviewBadge,
  usageLine,
  withLatestDecision,
} from "./libraryRules";
import {
  CenterMessage,
  ConfirmDialog,
  LIBRARY_HOME,
  LibraryPanel,
  LockedPackageRow,
  MessageDialog,
  SectionTitle,
  Spinner,
  packagePath,
  useAccessRequester,
} from "./LibraryKit";

const actionCls = (tone) =>
  `inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-sm transition-colors disabled:opacity-50 ${
    tone === "danger"
      ? `${cls.danger} hover:bg-terakkiError/10 dark:hover:bg-white/[0.06]`
      : tone === "muted"
        ? `${cls.muted} ${cls.rowHover}`
        : `${cls.accent} ${cls.rowHover}`
  }`;

function Action({ Icon, label, onClick, tone, disabled, iconSx }) {
  return (
    <button type="button" className={actionCls(tone)} onClick={onClick} disabled={disabled}>
      <Icon sx={{ fontSize: 18, ...iconSx }} aria-hidden />
      {label}
    </button>
  );
}

function PersonRow({ title, meta }) {
  const initials = String(title || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toLocaleUpperCase("tr");
  return (
    <li className={`flex items-center gap-3 px-4 py-2.5 ${cls.row}`}>
      <span className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-xs ${cls.avatar}`}>
        {initials || "?"}
      </span>
      <span className="min-w-0 flex-1">
        <span className={`block text-sm truncate ${cls.title}`}>{title}</span>
        {meta ? <span className={`block text-xs ${cls.muted}`}>{meta}</span> : null}
      </span>
    </li>
  );
}

function LinkRow({ to, Icon, title, meta }) {
  return (
    <li>
      <Link to={to} className={`flex items-center gap-3 px-4 py-2.5 ${cls.row} ${cls.rowHover}`}>
        <span className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${cls.avatar}`}>
          <Icon sx={{ fontSize: 16 }} aria-hidden />
        </span>
        <span className="min-w-0 flex-1">
          <span className={`block text-sm truncate ${cls.title}`}>{title}</span>
          {meta ? <span className={`block text-xs truncate ${cls.muted}`}>{meta}</span> : null}
        </span>
        <ChevronRightIcon fontSize="small" className={cls.muted} />
      </Link>
    </li>
  );
}

/**
 * Paket sayfası (mobil library/[id]): bilgiler, onay rozeti, "çatallandığı
 * paket" bağlantısı; Yıldızla, Çatalla, sahibe Düzenle ve Sil, başkasına
 * Bildir, çatal sahibine "Değişiklikleri öner"; sahibe Onaya gönder, Onaydan
 * geri çek ya da Yayından kaldır. Sahip önerileri ve yıldızlayanları görür;
 * çatallar herkese; en altta doğru cevaplarıyla sorular. Kilitli pakette
 * yalnız özet ve izin durumu.
 */
export default function PackageDetailPage() {
  const { id } = useParams();
  const { t } = useScriptMode();
  const toast = useToast();
  const navigate = useNavigate();
  const { data: pkg, isLoading, isError, error, refetch } = usePackageDetailQuery({ packageId: id });
  const ready = !!pkg && !pkg.locked;
  const { data: proposals } = usePackageProposalsQuery({ packageId: id }, { skip: !ready || !pkg?.isMine });
  const { data: stargazers } = usePackageStargazersQuery({ packageId: id }, { skip: !ready || !pkg?.isMine });
  const { data: forks, isFetching: forksLoading } = usePackageForksQuery(
    { packageId: id },
    { skip: !ready || !(pkg?.forkCount > 0) }
  );
  const needOutgoing = !!pkg && (pkg.locked || (pkg.forkCount > 0 && !pkg.isMine));
  const { data: outgoing = [] } = useAccessRequestsQuery({ role: "outgoing" }, { skip: !needOutgoing });
  const { requesting, request } = useAccessRequester();
  // İzin istenen kilitli satırlar (paket ya da çatal), sunucu yeniden sorulana kadar.
  const [patched, setPatched] = useState({});

  const [star] = useStarPackageMutation();
  const [fork] = useForkPackageMutation();
  const [remove] = useDeletePackageMutation();
  const [report] = useReportPackageMutation();
  const [submit] = useSubmitPackageMutation();
  const [createProposal] = useCreateProposalMutation();
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [proposeOpen, setProposeOpen] = useState(false);
  const inFlight = useRef(false);

  // Aynı anda tek işlem: çift tıklama ikinci isteği göndermesin.
  const run = async (task) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    try {
      const result = await task();
      if (result?.error) toast.error(result.error.message || t("lib_action_failed"));
      return result;
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  if (isLoading) {
    return (
      <LibraryPanel title={t("lib_package")} back={LIBRARY_HOME}>
        <Spinner />
      </LibraryPanel>
    );
  }
  if (isError || !pkg) {
    return (
      <LibraryPanel title={t("lib_package")} back={LIBRARY_HOME}>
        <CenterMessage text={error?.message || t("mzq_package_failed")} danger onRetry={refetch} error={error} />
      </LibraryPanel>
    );
  }

  if (pkg.locked) {
    const shown = withLatestDecision(patched[pkg.id] ? { ...pkg, ...patched[pkg.id] } : pkg, outgoing);
    return (
      <LibraryPanel title={pkg.title} back={LIBRARY_HOME}>
        <ul className="tk-stagger">
          <LockedPackageRow
            pkg={shown}
            requesting={requesting === pkg.id}
            onRequest={() => request(shown, (u) => setPatched((p) => ({ ...p, [pkg.id]: u })))}
          />
        </ul>
        <p className={`px-4 py-4 text-sm ${cls.muted}`}>{t("lib_locked_hint")}</p>
      </LibraryPanel>
    );
  }

  const questions = pkg.questions || [];
  const badge = reviewBadge(pkg);
  const usage = usageLine(pkg.usage, t, { always: true });
  const reviewAction = ownerReviewAction(pkg);
  const meta = [
    pkg.isMine ? null : pkg.ownerName,
    `${pkg.questionCount ?? questions.length} ${t("lib_word_question")}`,
    `${t("lib_version")} ${pkg.version ?? 1}`,
    `${pkg.starCount ?? 0} ${t("lib_word_star")}`,
    `${pkg.forkCount ?? 0} ${t("lib_word_fork")}`,
  ]
    .filter(Boolean)
    .join(" · ");

  const toggleStar = () => run(() => star({ packageId: pkg.id, starred: !pkg.starredByMe }));

  const doFork = () =>
    run(async () => {
      const result = await fork({ packageId: pkg.id });
      const created = result.data?.data?.package;
      if (created?.id) {
        toast.success(t("lib_forked"));
        navigate(packagePath(created.id));
      }
      return result;
    });

  const setSubmitted = (submitted) =>
    run(async () => {
      const result = await submit({ packageId: pkg.id, submitted });
      setConfirm(null);
      if (!result.error) toast.success(t(submitted ? "lib_submitted" : "lib_made_private"));
      return result;
    });

  const confirmTexts = {
    delete: { title: t("lib_delete_title"), text: t("lib_delete_text"), label: t("lib_delete"), danger: true },
    report: { title: t("lib_report_title"), text: t("lib_report_text"), label: t("lib_report"), danger: true },
    unpublish: { title: t("lib_unpublish"), text: t("lib_unpublish_text"), label: t("lib_unpublish_confirm"), danger: true },
    withdraw: { title: t("lib_withdraw_review"), text: t("lib_withdraw_review_text"), label: t("lib_withdraw_review") },
  };
  const c = confirm ? confirmTexts[confirm] : null;

  const onConfirm = () => {
    if (confirm === "delete") {
      run(async () => {
        const result = await remove({ packageId: pkg.id });
        setConfirm(null);
        if (!result.error) {
          toast.success(t("lib_deleted"));
          navigate(LIBRARY_HOME, { replace: true });
        }
        return result;
      });
    } else if (confirm === "report") {
      run(async () => {
        const result = await report({ packageId: pkg.id });
        setConfirm(null);
        if (!result.error) toast.success(t("lib_reported"));
        return result;
      });
    } else if (confirm === "unpublish" || confirm === "withdraw") {
      setSubmitted(false);
    }
  };

  const propose = (message) =>
    run(async () => {
      const result = await createProposal({ forkId: pkg.id, message });
      if (!result.error) {
        setProposeOpen(false);
        toast.success(t("lib_proposal_sent"));
      }
      return result;
    });

  const BadgeIcon = pkg.recommended ? MilitaryTechOutlinedIcon : pkg.verified ? VerifiedOutlinedIcon : HourglassEmptyIcon;

  return (
    <LibraryPanel title={t("lib_package")} back={LIBRARY_HOME}>
      <div className="px-4 pt-4 pb-3 flex flex-col gap-2">
        <h2 className={`text-xl font-Medium leading-snug break-words ${cls.title}`}>{pkg.title}</h2>
        {badge ? (
          <span className={`flex items-center gap-1.5 text-sm ${pkg.verified ? cls.accent : cls.muted}`}>
            <BadgeIcon sx={{ fontSize: 17 }} aria-hidden />
            {t(badge)}
          </span>
        ) : null}
        <p className={`text-sm ${cls.muted}`}>{meta}</p>
        {usage ? <p className={`text-sm ${cls.muted}`}>{usage}</p> : null}
        {pkg.forkedFrom ? (
          <Link
            to={packagePath(pkg.forkedFrom.packageId)}
            className={`flex items-center gap-1.5 text-sm min-w-0 ${cls.accent} hover:underline`}
          >
            <CallSplitIcon sx={{ fontSize: 16 }} aria-hidden />
            <span className="truncate">
              {`${t("lib_forked_from")}: ${pkg.forkedFrom.title}${pkg.forkedFrom.ownerName ? ` · ${pkg.forkedFrom.ownerName}` : ""}`}
            </span>
          </Link>
        ) : null}
        {pkg.isMine && pkg.verified === false ? (
          <p className={`text-sm ${cls.muted}`}>
            {t(pkg.reviewState === "private" ? "lib_private_hint" : "lib_pending_hint")}
          </p>
        ) : null}
        {pkg.description ? <p className={`text-base whitespace-pre-line break-words ${cls.title}`}>{pkg.description}</p> : null}
        {pkg.tags?.length ? (
          <div className="flex flex-wrap gap-1.5">
            {pkg.tags.map((tag) => (
              <SoftTag key={tag}>{tag}</SoftTag>
            ))}
          </div>
        ) : null}
      </div>

      <div className={`px-2 pb-2 flex flex-wrap gap-1 ${cls.row}`}>
        <Action
          Icon={pkg.starredByMe ? StarIcon : StarBorderIcon}
          iconSx={pkg.starredByMe ? { color: "#E8A23A" } : undefined}
          label={t(pkg.starredByMe ? "lib_starred" : "lib_star")}
          onClick={toggleStar}
          disabled={busy}
        />
        {pkg.isMine ? null : <Action Icon={CallSplitIcon} label={t("lib_fork")} onClick={doFork} disabled={busy} />}
        {pkg.isMine ? (
          <Action
            Icon={EditOutlinedIcon}
            label={t("lib_edit")}
            onClick={() => navigate(`${packagePath(pkg.id)}/duzenle`)}
            disabled={busy}
          />
        ) : null}
        {pkg.isMine && pkg.forkedFrom ? (
          <Action Icon={CallMergeIcon} label={t("lib_propose")} onClick={() => setProposeOpen(true)} disabled={busy} />
        ) : null}
        {reviewAction === "submit" ? (
          <Action Icon={SendOutlinedIcon} label={t("lib_submit")} onClick={() => setSubmitted(true)} disabled={busy} />
        ) : reviewAction === "withdraw" ? (
          <Action Icon={UndoIcon} label={t("lib_withdraw_review")} tone="muted" onClick={() => setConfirm("withdraw")} disabled={busy} />
        ) : reviewAction === "unpublish" ? (
          <Action
            Icon={VisibilityOffOutlinedIcon}
            label={t("lib_unpublish")}
            tone="muted"
            onClick={() => setConfirm("unpublish")}
            disabled={busy}
          />
        ) : null}
        {pkg.isMine ? (
          <Action Icon={DeleteOutlineIcon} label={t("lib_delete")} tone="danger" onClick={() => setConfirm("delete")} disabled={busy} />
        ) : (
          <Action Icon={FlagOutlinedIcon} label={t("lib_report")} tone="muted" onClick={() => setConfirm("report")} disabled={busy} />
        )}
      </div>

      {pkg.isMine && proposals ? (
        <>
          <SectionTitle>{`${t(pkg.forkedFrom ? "lib_my_proposals" : "lib_proposals")} · ${proposals.length}`}</SectionTitle>
          {proposals.length === 0 ? (
            <p className={`px-4 py-3 text-sm ${cls.muted}`}>{t("lib_no_proposals")}</p>
          ) : (
            <ul className="tk-stagger">
              {proposals.map((r) => (
                <LinkRow
                  key={r.id}
                  to={`${LIBRARY_HOME}/oneri/${r.id}`}
                  Icon={CallMergeIcon}
                  // Bu çataldan giden öneride asıl paketin adı, gelen öneride önerenin adı.
                  title={
                    r.forkId === pkg.id ? r.targetTitle || pkg.forkedFrom?.title || "" : r.proposerName || r.forkTitle
                  }
                  meta={[t(PROPOSAL_STATUS_KEY[r.status] || "lib_prop_open"), formatDay(r.createdAt), r.message]
                    .filter(Boolean)
                    .join(" · ")}
                />
              ))}
            </ul>
          )}
        </>
      ) : null}

      {pkg.isMine && stargazers ? (
        <>
          <SectionTitle>{`${t("lib_stargazers")} · ${stargazers.length}`}</SectionTitle>
          {stargazers.length === 0 ? (
            <p className={`px-4 py-3 text-sm ${cls.muted}`}>{t("lib_no_stargazers")}</p>
          ) : (
            <ul className="tk-stagger">
              {stargazers.map((u) => (
                <PersonRow key={u.id} title={`${u.name || ""} ${u.surname || ""}`.trim()} meta={formatDay(u.starredAt)} />
              ))}
            </ul>
          )}
        </>
      ) : null}

      {pkg.forkCount > 0 ? (
        <>
          <SectionTitle>{`${t("lib_forks")} · ${pkg.forkCount}`}</SectionTitle>
          {!forks && forksLoading ? (
            <Spinner />
          ) : !forks || forks.length === 0 ? (
            <p className={`px-4 py-3 text-sm ${cls.muted}`}>{t("lib_no_forks")}</p>
          ) : (
            <ul className="tk-stagger">
              {forks.map((raw) => {
                const f = patched[raw.id] ? { ...raw, ...patched[raw.id] } : raw;
                return f.locked ? (
                  <LockedPackageRow
                    key={f.id}
                    pkg={withLatestDecision(f, outgoing)}
                    requesting={requesting === f.id}
                    onRequest={() => request(f, (u) => setPatched((p) => ({ ...p, [f.id]: u })))}
                    to={packagePath(f.id)}
                  />
                ) : (
                  <LinkRow key={f.id} to={packagePath(f.id)} Icon={CallSplitIcon} title={f.title} meta={forkMeta(f, t)} />
                );
              })}
            </ul>
          )}
        </>
      ) : null}

      <SectionTitle>{`${t("lib_questions")} · ${questions.length}`}</SectionTitle>
      {questions.length === 0 ? (
        <p className={`px-4 py-3 text-sm ${cls.muted}`}>{t("lib_no_questions")}</p>
      ) : (
        <div className="px-3 pb-4">
          <QuestionRows questions={questions} showAnswers />
        </div>
      )}

      <ConfirmDialog
        open={!!c}
        title={c?.title}
        text={c?.text}
        confirmLabel={c?.label}
        danger={c?.danger}
        busy={busy}
        onConfirm={onConfirm}
        onClose={() => setConfirm(null)}
      />
      <MessageDialog
        open={proposeOpen}
        title={t("lib_propose")}
        hint={t("lib_propose_hint")}
        placeholder={t("lib_message_optional")}
        sendLabel={t("lib_send_proposal")}
        maxLength={PROPOSAL_MESSAGE_MAX}
        busy={busy}
        onSend={propose}
        onClose={() => setProposeOpen(false)}
      />
    </LibraryPanel>
  );
}
